import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import integrationsService from '../../services/integrationsService';
import Button from '../shared/Button';
import { stateOf } from './StreamingClientPanel';
import { formatDateTime } from '../../utils/datetime';
import { apiErrorMessage } from '../../utils/apiError';
import './StreamingClients.css';

// THE TEMPLATE'S STREAMING CLIENTS (V616; on the template since V686) — the Stream half of the template's OHIP events
// page (TemplateEventDeliveryPage), shown when the template streams its events rather than polling them.
//
// ONE CLIENT PER HOTEL. Each hotel implementing the template streams with its own source credential (chain and hotel
// come from it), so each has its own client (pl-<implementation id>). This screen shows every hotel's client as
// Kubernetes has it now and deploys them — every active hotel at once, or one. Stopping, settings and logs stay in
// Admin › Infra › Streaming Clients, where every client on the platform is.
//
// Read by anyone who can see the template (their own hotels); deployed by a superadmin only (the API refuses anyone else).

const LOG_LEVELS = ['trace', 'debug', 'info', 'warn', 'error'];

const TemplateStreamingClientsSection = ({ templateId: id }) => {
    const [data, setData] = useState(null);
    const [logLevel, setLogLevel] = useState('info');
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            setData(await integrationsService.getStreamingClients(id));
        } catch (e) {
            setError(apiErrorMessage(e));
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const deploy = async (what, implementationIds) => {
        setBusy(what);
        setError(null);
        try {
            setData(await integrationsService.deployStreamingClients(id, { logLevel, ...(implementationIds ? { implementationIds } : {}) }));
        } catch (e) {
            setError(apiErrorMessage(e));
        } finally {
            setBusy(null);
        }
    };

    const hotels = (data && data.hotels) || [];
    const active = hotels.filter(h => h.status === 'active');
    const canEdit = !!(data && data.canEdit);

    return (
        <>
            {error && <div className="os-msg os-msg-error">{error}</div>}
            {!data && !error && <p className="cm-message">Loading…</p>}

            {data && (
                <div className="stc-panel">
                    <p className="cm-message">
                        One streaming client per hotel implementing this template, each signing in with that hotel&rsquo;s
                        source credential.{canEdit && <> <Link className="sa-only" to="/admin/streaming-clients">All streaming clients</Link></>}
                    </p>

                    {canEdit && <div className="sa-only-panel stc-panel">
                    <div className="scq-form stc-settings">
                        <label>Log level
                            <select value={logLevel} onChange={e => setLogLevel(e.target.value)}>
                                {LOG_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                        </label>
                    </div>
                    <div className="stc-actions">
                        <Button variant="primary" disabled={!!busy || !active.length} onClick={() => deploy('all')}
                                title={active.length ? undefined : 'No implementation of this template is active'}>
                            {busy === 'all' ? 'Deploying…' : `Deploy for every active hotel (${active.length})`}
                        </Button>
                    </div>
                    </div>}

                    <table className="acp-table">
                        <thead>
                            <tr><th>Hotel</th><th>Implementation</th><th>Client</th><th>Deployment</th><th>Deployed</th><th></th></tr>
                        </thead>
                        <tbody>
                            {hotels.length === 0 && (
                                <tr><td colSpan={6}>No hotel implements this template yet.</td></tr>
                            )}
                            {hotels.map(h => {
                                const st = h.stream ? stateOf(h.stream) : null;
                                const dep = h.stream && h.stream.deployment;
                                return (
                                    <tr key={h.id}>
                                        <td>{h.hotelname || h.name}</td>
                                        <td>{h.status}</td>
                                        <td>{st ? <span className={`scq-status scq-status-${st.cls}`}>{st.label}</span> : '—'}
                                            {h.error && <div className="scq-meta">{h.error}</div>}
                                            {h.deploy && !h.deploy.ok && <div className="scq-meta">deploy failed: {h.deploy.error}</div>}
                                            {dep && dep.reason && <div className="scq-meta">{dep.reason}</div>}</td>
                                        <td>{dep ? <span className="scq-mono">{dep.namespace}/{dep.deployment}</span> : '—'}</td>
                                        <td>{dep ? formatDateTime(dep.deployedAt) : '—'}
                                            {dep && dep.deployedBy && <div className="scq-meta">{dep.deployedBy}</div>}</td>
                                        <td>
                                            {canEdit && <Button variant="secondary" className="sa-only" disabled={!!busy} onClick={() => deploy(h.id, [h.id])}>
                                                {busy === h.id ? 'Deploying…' : dep ? 'Redeploy' : 'Deploy'}
                                            </Button>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default TemplateStreamingClientsSection;
